import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, Clock, Activity, FileText, 
  Search, Bell, LogOut, Calendar, XCircle, AlertCircle
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import config from '../../config';
import nerusLogo from '../../Assests/nerussmall.png';

const Dashboard = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Protect Route & Fetch Data
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchRequests = async () => {
      try {
        const response = await fetch(`${config.API_URL}/requests/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch requests');
        }

        const data = await response.json();
        // Sort by id descending so newest are first
        const sortedData = data.sort((a, b) => b.id - a.id);
        setRequests(sortedData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Compute Stats
  const activeRequests = requests.filter(r => (r.status || '').toLowerCase() === 'active').length;
  const pendingRequests = requests.filter(r => (r.status || 'pending').toLowerCase() === 'pending').length;
  const closedRequests = requests.filter(r => (r.status || '').toLowerCase() === 'closed').length;
  const blockedRequests = requests.filter(r => (r.status || '').toLowerCase() === 'blocked').length;
  const totalRequests = requests.length;
  
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const thisMonthRequests = requests.filter(r => {
    const date = new Date(r.timestamp);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  }).length;

  const filteredRequests = requests.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.id.toString().includes(query) ||
      (req.name || '').toLowerCase().includes(query) ||
      (req.email || '').toLowerCase().includes(query) ||
      (req.mobile || '').includes(query)
    );
  });

  // Donut Chart Data
  const chartData = [
    { name: 'Pending', value: pendingRequests, color: '#f59e0b' }, // amber-500
    { name: 'Active', value: activeRequests, color: '#38bdf8' }, // sky-400
    { name: 'Closed', value: closedRequests, color: '#34d399' }, // emerald-400
    { name: 'Blocked', value: blockedRequests, color: '#ef4444' }, // red-500
  ].filter(d => d.value > 0);

  if (chartData.length === 0) {
    chartData.push({ name: 'No Data', value: 1, color: '#334155' });
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center space-y-6">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white/5 p-3 shadow-[0_0_40px_rgba(34,211,238,0.1)] relative">
          <div className="absolute inset-0 border-t-2 border-cyan-400 rounded-2xl animate-spin"></div>
          <img src={nerusLogo} alt="Loading..." className="w-full h-full object-contain animate-pulse" />
        </div>
        <div className="text-cyan-400/60 font-bold tracking-widest text-sm uppercase animate-pulse">Loading Dashboard</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-cyan-500/30">
      {/* Top Navbar */}
      <nav className="border-b border-white/10 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center bg-white/10">
                <img src={nerusLogo} alt="Nerus Logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-xl font-black text-white tracking-tight">NERUS<span className="text-cyan-400">ADMIN</span></span>
            </div>
            <div className="flex items-center gap-4">
              <button className="text-slate-400 hover:text-white transition-colors relative">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <div className="h-8 w-px bg-white/10 mx-2"></div>
              <button 
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-black text-white mb-2">Command Center</h1>
          <p className="text-slate-400">Monitor incoming requests and portfolio inquiries in real-time.</p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 font-medium">
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-white">
              <FileText className="w-16 h-16" />
            </div>
            <div className="relative z-10">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Total</p>
              <p className="text-3xl font-black text-white">{totalRequests}</p>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-white">
              <Calendar className="w-16 h-16" />
            </div>
            <div className="relative z-10">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">This Month</p>
              <p className="text-3xl font-black text-white">{thisMonthRequests}</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 border border-amber-500/20 rounded-2xl p-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-amber-400">
              <Clock className="w-16 h-16" />
            </div>
            <div className="relative z-10">
              <p className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-1">Pending</p>
              <p className="text-3xl font-black text-white">{pendingRequests}</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/20 rounded-2xl p-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-cyan-400">
              <Activity className="w-16 h-16" />
            </div>
            <div className="relative z-10">
              <p className="text-xs font-bold text-cyan-400 uppercase tracking-wider mb-1">Active</p>
              <p className="text-3xl font-black text-white">{activeRequests}</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-600/10 border border-emerald-500/20 rounded-2xl p-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-emerald-400">
              <CheckCircle className="w-16 h-16" />
            </div>
            <div className="relative z-10">
              <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1">Closed</p>
              <p className="text-3xl font-black text-white">{closedRequests}</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500/10 to-rose-600/10 border border-red-500/20 rounded-2xl p-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-red-400">
              <XCircle className="w-16 h-16" />
            </div>
            <div className="relative z-10">
              <p className="text-xs font-bold text-red-400 uppercase tracking-wider mb-1">Blocked</p>
              <p className="text-3xl font-black text-white">{blockedRequests}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Charts Section */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 lg:col-span-1 flex flex-col">
            <h2 className="text-lg font-bold text-white mb-6">Status Distribution</h2>
            <div className="flex-1 min-h-[300px] flex items-center justify-center relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={110}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '0.5rem', color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Center Text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-3xl font-black text-white">{totalRequests}</span>
                <span className="text-xs text-slate-400 font-bold uppercase tracking-widest">Total</span>
              </div>
            </div>
            
            <div className="mt-6 space-y-3">
              {chartData.map(item => item.name !== 'No Data' && (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-sm text-slate-300 font-medium">{item.name}</span>
                  </div>
                  <span className="text-sm font-bold text-white">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 lg:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-white">Recent Requests</h2>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input 
                  type="text" 
                  placeholder="Search Ticket ID, Name..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-slate-900/50 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 w-64"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                    <th className="pb-3 font-bold">Ticket ID</th>
                    <th className="pb-3 font-bold">Client Name</th>
                    <th className="pb-3 font-bold">Contact</th>
                    <th className="pb-3 font-bold">Date</th>
                    <th className="pb-3 font-bold text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="py-8 text-center text-slate-500">No requests found.</td>
                    </tr>
                  ) : (
                    filteredRequests.map((req) => (
                      <tr 
                        key={req.id} 
                        onClick={() => navigate(`/ticket/${req.id}`)}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      >
                        <td className="py-4 pr-4 font-mono text-cyan-400 font-bold">
                          #TKT-{req.id}
                        </td>
                        <td className="py-4 pr-4">
                          <div className="font-bold text-white">{req.name}</div>
                          <div className="text-xs text-slate-400">{req.email}</div>
                        </td>
                        <td className="py-4 text-slate-300 pr-4">{req.mobile}</td>
                        <td className="py-4 text-slate-400 whitespace-nowrap">
                          {new Date(req.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="py-4 text-right">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${
                            (req.status || 'pending').toLowerCase() === 'closed' 
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                              : (req.status || 'pending').toLowerCase() === 'blocked'
                              ? 'bg-red-500/10 text-red-400 border-red-500/20'
                              : (req.status || 'pending').toLowerCase() === 'active'
                              ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'
                              : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                          }`}>
                            {req.status ? req.status.charAt(0).toUpperCase() + req.status.slice(1) : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
